'use client'
import { useState } from 'react'
import { AxiosError } from 'axios'
import { useSWRConfig } from 'swr'
import { useCustomRouter, useSetAuthToken } from '@/root/shared-hooks'
import { signIn, signInByEmailOrPhone, logout } from '@/root/shared-api/auth/authService'
import type { AuthError, LogInParams } from '@/root/shared-types/api/auth'
import { AUTH_ERROR_CODES, AUTH_ERROR_MESSAGES } from '@/root/shared-types/api/auth'

const parseAxiosAuthError = (err: AxiosError<AuthError>): AuthError => {
  const responseData = err.response?.data
  return {
    message: AUTH_ERROR_MESSAGES.AUTH_ERROR,
    code: responseData?.code ?? AUTH_ERROR_CODES.AUTH_FAILED,
  }
}

export const useAuth = () => {
  const { saveToken, resetToken } = useSetAuthToken()
  const { push, locale } = useCustomRouter()
  const { mutate: globalMutate } = useSWRConfig()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<AuthError | null>(null)

  const logIn = async ({ body, method = 'email', rememberMe }: LogInParams): Promise<{ success: boolean; error?: AuthError }> => {
    setIsLoading(true)
    setError(null)

    try {
      const request = method === 'emailOrPhone' ? signInByEmailOrPhone : signIn
      const { data } = await request({ ...body, rememberMe })
      if (data?.token) {
        await saveToken(data.token, rememberMe)
        await push(`/${locale}/my/details`)
        return { success: true }
      }

      const authError: AuthError = {
        message: AUTH_ERROR_MESSAGES.AUTH_ERROR,
        code: AUTH_ERROR_CODES.AUTH_FAILED,
      }
      setError(authError)
      return { success: false, error: authError }
    } catch (err) {
      const authError: AuthError = err instanceof AxiosError
        ? parseAxiosAuthError(err)
        : { message: AUTH_ERROR_MESSAGES.UNKNOWN_ERROR, code: AUTH_ERROR_CODES.UNKNOWN }

      setError(authError)
      return { success: false, error: authError }
    } finally {
      setIsLoading(false)
    }
  }

  const logOut = () => {
    void resetToken()
    globalMutate(() => true, undefined, { revalidate: false })
    push(`/${locale}`)
    logout().catch(() => {})
  }

  return { logIn, logOut, isLoading, error }
}
